import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faQuoteLeft, 
  faTrash, 
  faPlus, 
  faChartLine,
  faLink,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  faSpotify, 
  faYoutube, 
  faInstagram, 
  faTwitter, 
  faTiktok, 
  faFacebook 
} from '@fortawesome/free-brands-svg-icons';
import styles from './EPKBuilder.module.css';

const StatsStep = ({ data, updateData, errors, mode }) => {
  const [stats, setStats] = useState(() => ({
    spotify: {
      monthlyListeners: data.stats?.spotify?.monthlyListeners || '',
      totalStreams: data.stats?.spotify?.totalStreams || ''
    },
    youtube: {
      totalViews: data.stats?.youtube?.totalViews || '',
      topVideoViews: data.stats?.youtube?.topVideoViews || ''
    },
    instagram: data.stats?.instagram || '',
    tiktok: data.stats?.tiktok || '',
    twitter: data.stats?.twitter || '',
    facebook: data.stats?.facebook || ''
  }));
  
  const [pressQuotes, setPressQuotes] = useState(() => {
    if (data.pressQuotes && data.pressQuotes.length > 0) {
      return data.pressQuotes;
    }
    return [{ quote: '', source: '', link: '' }];
  });
  
  const [pressCoverage, setPressCoverage] = useState(() => {
    if (data.pressCoverage && data.pressCoverage.length > 0) {
      return data.pressCoverage;
    }
    return [{ title: '', publication: '', date: '', link: '' }];
  });
  
  // Use refs to prevent update loops
  const initialRender = useRef(true);
  const prevStatsRef = useRef(stats);
  const prevPressQuotesRef = useRef(pressQuotes);
  const prevPressCoverageRef = useRef(pressCoverage);
  
  // Update parent component when data changes
  useEffect(() => {
    // Skip the update on the initial render
    if (initialRender.current) {
      initialRender.current = false;
      prevStatsRef.current = stats;
      prevPressQuotesRef.current = pressQuotes;
      prevPressCoverageRef.current = pressCoverage;
      return;
    }
    
    // Only update if data has actually changed
    if (JSON.stringify(prevStatsRef.current) !== JSON.stringify(stats) || 
        JSON.stringify(prevPressQuotesRef.current) !== JSON.stringify(pressQuotes) ||
        JSON.stringify(prevPressCoverageRef.current) !== JSON.stringify(pressCoverage)) {
      
      console.log('StatsStep: Syncing with parent component', { stats, pressQuotes, pressCoverage });
      updateData({
        stats,
        pressQuotes,
        pressCoverage
      });
      
      // Update refs
      prevStatsRef.current = stats;
      prevPressQuotesRef.current = pressQuotes;
      prevPressCoverageRef.current = pressCoverage;
    }
  }, [stats, pressQuotes, pressCoverage, updateData]);
  
  // Handle stats input change
  const handleStatsChange = (platform, field, value) => {
    if (mode === 'view') return;
    
    // Format numbers by removing non-numeric characters
    if (field.includes('Listeners') || field.includes('Streams') || field.includes('Views') || 
        platform === 'instagram' || platform === 'tiktok' || platform === 'twitter' || platform === 'facebook') {
      value = value.replace(/\D/g, '');
    }
    
    setStats(prev => {
      if (platform === 'spotify' || platform === 'youtube') {
        return {
          ...prev,
          [platform]: {
            ...prev[platform],
            [field]: value
          }
        };
      } else {
        return {
          ...prev,
          [platform]: value
        };
      }
    });
  };
  
  // Handle press quote changes
  const handleQuoteChange = (index, field, value) => {
    if (mode === 'view') return;
    
    const updatedQuotes = [...pressQuotes];
    updatedQuotes[index][field] = value;
    setPressQuotes(updatedQuotes);
  };
  
  // Handle press coverage changes
  const handleCoverageChange = (index, field, value) => {
    if (mode === 'view') return;
    
    const updatedCoverage = [...pressCoverage];
    updatedCoverage[index][field] = value;
    setPressCoverage(updatedCoverage);
  };
  
  // Add new press quote
  const addPressQuote = () => {
    if (mode === 'view') return;
    
    setPressQuotes([
      ...pressQuotes,
      { quote: '', source: '', link: '' }
    ]);
  };
  
  // Remove press quote
  const removePressQuote = (index) => {
    if (mode === 'view') return;
    
    // Don't remove if it's the last one
    if (pressQuotes.length <= 1) return;
    
    const updatedQuotes = [...pressQuotes];
    updatedQuotes.splice(index, 1);
    setPressQuotes(updatedQuotes);
  };
  
  // Add new press coverage
  const addPressCoverage = () => {
    if (mode === 'view') return;
    
    setPressCoverage([
      ...pressCoverage,
      { title: '', publication: '', date: '', link: '' }
    ]);
  };
  
  // Remove press coverage
  const removePressCoverage = (index) => {
    if (mode === 'view') return;
    
    // Don't remove if it's the last one
    if (pressCoverage.length <= 1) return;
    
    const updatedCoverage = [...pressCoverage];
    updatedCoverage.splice(index, 1);
    setPressCoverage(updatedCoverage);
  };
  
  // Format numbers for display
  const formatNumber = (num) => {
    if (!num) return '';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  
  return (
    <div className={`${styles.formSection} ${mode === 'view' ? styles.viewMode : ''}`}>
      <h2 className={styles.sectionTitle}>Stats & Press</h2>
      <p className={styles.sectionDescription}>
        Add your streaming numbers and press coverage to showcase your reach and media presence.
      </p>
      
      {/* Streaming Stats Section */}
      <div className={styles.statsSection}>
        <h3>Streaming & Social Stats</h3>
        <p className={styles.sectionSubdescription}>
          These metrics help promoters and venues understand your audience reach.
        </p>
        
        <div className={styles.formGrid}>
          {/* Spotify Stats */}
          <div className={styles.formGroup}>
            <label>
              <FontAwesomeIcon icon={faSpotify} /> Spotify Monthly Listeners
            </label>
            <input
              type="text"
              value={formatNumber(stats.spotify.monthlyListeners)}
              onChange={(e) => handleStatsChange('spotify', 'monthlyListeners', e.target.value)}
              placeholder="e.g., 10000"
              disabled={mode === 'view'}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label>
              <FontAwesomeIcon icon={faSpotify} /> Spotify Total Streams
            </label>
            <input
              type="text"
              value={formatNumber(stats.spotify.totalStreams)}
              onChange={(e) => handleStatsChange('spotify', 'totalStreams', e.target.value)}
              placeholder="e.g., 1000000"
              disabled={mode === 'view'}
            />
          </div>
          
          {/* YouTube Stats */}
          <div className={styles.formGroup}>
            <label>
              <FontAwesomeIcon icon={faYoutube} /> YouTube Total Views
            </label>
            <input
              type="text"
              value={formatNumber(stats.youtube.totalViews)}
              onChange={(e) => handleStatsChange('youtube', 'totalViews', e.target.value)}
              placeholder="e.g., 500000"
              disabled={mode === 'view'}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label>
              <FontAwesomeIcon icon={faYoutube} /> Top Video Views
            </label>
            <input
              type="text"
              value={formatNumber(stats.youtube.topVideoViews)}
              onChange={(e) => handleStatsChange('youtube', 'topVideoViews', e.target.value)}
              placeholder="e.g., 250000"
              disabled={mode === 'view'}
            />
          </div>
          
          {/* Social Media Stats */}
          <div className={styles.formGroup}>
            <label>
              <FontAwesomeIcon icon={faInstagram} /> Instagram Followers
            </label>
            <input
              type="text"
              value={formatNumber(stats.instagram)}
              onChange={(e) => handleStatsChange('instagram', '', e.target.value)}
              placeholder="e.g., 50000"
              disabled={mode === 'view'}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label>
              <FontAwesomeIcon icon={faTiktok} /> TikTok Followers
            </label>
            <input
              type="text"
              value={formatNumber(stats.tiktok)}
              onChange={(e) => handleStatsChange('tiktok', '', e.target.value)}
              placeholder="e.g., 100000"
              disabled={mode === 'view'}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label>
              <FontAwesomeIcon icon={faTwitter} /> Twitter Followers
            </label>
            <input
              type="text"
              value={formatNumber(stats.twitter)}
              onChange={(e) => handleStatsChange('twitter', '', e.target.value)}
              placeholder="e.g., 25000"
              disabled={mode === 'view'}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label>
              <FontAwesomeIcon icon={faFacebook} /> Facebook Followers
            </label>
            <input
              type="text"
              value={formatNumber(stats.facebook)}
              onChange={(e) => handleStatsChange('facebook', '', e.target.value)}
              placeholder="e.g., 75000"
              disabled={mode === 'view'}
            />
          </div>
        </div>
      </div>
      
      {/* Press Quotes Section */}
      <div className={styles.pressQuotesSection}>
        <h3>Press Quotes</h3>
        <p className={styles.sectionSubdescription}>
          Include notable quotes from reviews and press coverage about your music.
        </p>
        
        {pressQuotes.map((quote, index) => (
          <div key={`quote-${index}`} className={styles.quoteItem}>
            <div className={styles.formGroup}>
              <label htmlFor={`quote-${index}`}>Quote</label>
              <textarea
                id={`quote-${index}`}
                value={quote.quote}
                onChange={(e) => handleQuoteChange(index, 'quote', e.target.value)}
                placeholder="Enter a quote from a review or article about your music"
                disabled={mode === 'view'}
              />
            </div>
            
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor={`source-${index}`}>Source</label>
                <input
                  type="text"
                  id={`source-${index}`}
                  value={quote.source}
                  onChange={(e) => handleQuoteChange(index, 'source', e.target.value)}
                  placeholder="Publication or reviewer name"
                  disabled={mode === 'view'}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor={`link-${index}`}>Link (optional)</label>
                <input
                  type="url"
                  id={`link-${index}`}
                  value={quote.link}
                  onChange={(e) => handleQuoteChange(index, 'link', e.target.value)}
                  placeholder="URL to the full review"
                  disabled={mode === 'view'}
                />
              </div>
            </div>
            
            {mode !== 'view' && (
              <button
                className={styles.deleteQuote}
                onClick={() => removePressQuote(index)}
                disabled={pressQuotes.length <= 1}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        ))}
        
        {mode !== 'view' && (
          <button 
            type="button" 
            className={styles.addItemBtn}
            onClick={addPressQuote}
            disabled={pressQuotes.length >= 5}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Another Quote
          </button>
        )}
      </div>
      
      {/* Press Coverage Section */}
      <div className={styles.pressCoverageSection}>
        <h3>Press Coverage</h3>
        <p className={styles.sectionSubdescription}>
          List articles, interviews, or features about you or your music.
        </p>
        
        {pressCoverage.map((coverage, index) => (
          <div key={`coverage-${index}`} className={styles.coverageItem}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor={`title-${index}`}>Article Title</label>
                <input
                  type="text"
                  id={`title-${index}`}
                  value={coverage.title}
                  onChange={(e) => handleCoverageChange(index, 'title', e.target.value)}
                  placeholder="Title of the article or feature"
                  disabled={mode === 'view'}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor={`publication-${index}`}>Publication</label>
                <input
                  type="text"
                  id={`publication-${index}`}
                  value={coverage.publication}
                  onChange={(e) => handleCoverageChange(index, 'publication', e.target.value)}
                  placeholder="Name of publication or website"
                  disabled={mode === 'view'}
                />
              </div>
            </div>
            
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor={`date-${index}`}>Date (optional)</label>
                <input
                  type="text"
                  id={`date-${index}`}
                  value={coverage.date}
                  onChange={(e) => handleCoverageChange(index, 'date', e.target.value)}
                  placeholder="e.g., March 2023"
                  disabled={mode === 'view'}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor={`coverageLink-${index}`}>Link</label>
                <input
                  type="url"
                  id={`coverageLink-${index}`}
                  value={coverage.link}
                  onChange={(e) => handleCoverageChange(index, 'link', e.target.value)}
                  placeholder="URL to the article"
                  disabled={mode === 'view'}
                />
              </div>
            </div>
            
            {mode !== 'view' && (
              <button
                className={styles.deleteQuote}
                onClick={() => removePressCoverage(index)}
                disabled={pressCoverage.length <= 1}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        ))}
        
        {mode !== 'view' && (
          <button 
            type="button" 
            className={styles.addItemBtn}
            onClick={addPressCoverage}
            disabled={pressCoverage.length >= 5}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Another Article
          </button>
        )}
      </div>
      
      {mode !== 'view' && (
        <div className={styles.tip}>
          <FontAwesomeIcon icon={faChartLine} className={styles.tipIcon} />
          <p>
            Tip: Even if your numbers aren't huge yet, including verifiable stats shows promoters your growing audience and potential.
          </p>
        </div>
      )}
    </div>
  );
};

export default StatsStep;